import {useEffect, useState} from 'react';
import {Alert, ScrollView, View} from 'react-native';
import {Button, RadioButton} from 'react-native-paper';
import {Text, Touchable} from '../../Components';
import {PageContainer} from '../../Containers';
import {useTheme} from '../../Hooks';
import Storage from '../../Utils/Storage';
import {
  AVAILABLE_LOCALITIES,
  AVAILABLE_REGIONS,
  DEFAULT_REGIONAL_PROFILE,
  REGION_STORAGE_KEY,
  SupportedLocality,
  SupportedRegion,
  parseRegionalProfile,
  serializeRegionalProfile,
} from '../../Utils/RegionalSettings';
import {RootRegionalSettingsScreenProps} from '../../Typings/NavigationTypes';
import getStyles from './RegionalSettings.styles';

type Props = RootRegionalSettingsScreenProps;

const RegionalSettings: React.FC<Props> = ({navigation}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [selectedCountry, setSelectedCountry] = useState<SupportedRegion>(
    DEFAULT_REGIONAL_PROFILE.country,
  );
  const [selectedLocality, setSelectedLocality] = useState<SupportedLocality | null>(
    DEFAULT_REGIONAL_PROFILE.locality,
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadSavedRegion = async () => {
      const storedRegion = await Storage.get(
        REGION_STORAGE_KEY,
        serializeRegionalProfile(DEFAULT_REGIONAL_PROFILE),
      );
      const regionalProfile = parseRegionalProfile(storedRegion);

      setSelectedCountry(regionalProfile.country);
      setSelectedLocality(regionalProfile.locality);
    };

    loadSavedRegion();
  }, []);

  const onCountrySelect = (country: SupportedRegion) => {
    if (country === selectedCountry) {
      return;
    }

    setSelectedCountry(country);
    setSelectedLocality(null);
  };

  const onSavePress = async () => {
    if (!selectedLocality) {
      Alert.alert('Incomplete selection', 'Please choose location type before saving.');
      return;
    }

    setIsSaving(true);
    const serializedValue = serializeRegionalProfile({
      country: selectedCountry,
      locality: selectedLocality,
    });
    const isSaved = await Storage.set(REGION_STORAGE_KEY, serializedValue);
    setIsSaving(false);

    if (!isSaved) {
      Alert.alert('Save failed', 'Unable to save current region. Please try again.');
      return;
    }

    Alert.alert('Saved', `Current region set to ${selectedCountry} (${selectedLocality}).`, [
      {
        text: 'OK',
        onPress: () => navigation.goBack(),
      },
    ]);
  };

  return (
    <PageContainer withSafeArea withPadding>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.card}>
          <Text weight="bold" fontSize={18}>
            Region
          </Text>

          <Text style={styles.intro}>
            Select country and locality for this device. This setting is saved locally and does not
            affect model inference yet.
          </Text>

          <Text weight="semiBold" style={styles.currentRegionText}>
            Current Region: {selectedCountry} ({selectedLocality || 'Not selected'})
          </Text>

          {AVAILABLE_REGIONS.map((region) => {
            const isSelected = selectedCountry === region;

            return (
              <Touchable key={region} onPress={() => onCountrySelect(region)}>
                <View style={[styles.optionRow, isSelected ? styles.selectedOptionRow : null]}>
                  <Text weight="medium" style={styles.optionLabel}>
                    {region}
                  </Text>

                  <RadioButton
                    value={region}
                    status={isSelected ? 'checked' : 'unchecked'}
                    onPress={() => onCountrySelect(region)}
                  />
                </View>
              </Touchable>
            );
          })}

          <Text weight="semiBold" style={styles.localityTitle}>
            Locality Type
          </Text>

          {AVAILABLE_LOCALITIES.map((locality) => {
            const isSelected = selectedLocality === locality;

            return (
              <Touchable key={locality} onPress={() => setSelectedLocality(locality)}>
                <View style={[styles.optionRow, isSelected ? styles.selectedOptionRow : null]}>
                  <Text weight="medium" style={styles.optionLabel}>
                    {locality}
                  </Text>

                  <RadioButton
                    value={locality}
                    status={isSelected ? 'checked' : 'unchecked'}
                    onPress={() => setSelectedLocality(locality)}
                  />
                </View>
              </Touchable>
            );
          })}

          <Button
            mode="contained"
            onPress={onSavePress}
            loading={isSaving}
            disabled={isSaving || !selectedLocality}
            style={styles.saveButton}
            buttonColor={theme.colors.primary}
            textColor={theme.colors.textContrast}
          >
            Save Region Profile
          </Button>
        </View>
      </ScrollView>
    </PageContainer>
  );
};

export default RegionalSettings;
